// The weekly calendar, drawn into any container: a page section, a host page's own modal,
// or this component's popup. Each mount gets its own shadow root.

import calendarCss from './calendar.css?raw';
import {
  DAY_NAMES,
  buildSchedule,
  escapeHtml,
  findNextClassId,
  formatResumeDate,
  formatTo12h,
  getWeekStartIndex,
  isClassNow,
  loadSchedule,
  localTimeOf,
  tagColors,
} from './schedule-data';
import { loadRatings } from './ratings';
import { openDetail, openVideo, pricingUrl } from './detail';

// Live Now / Next and this week's overrides move with the clock: re-render once a minute
// while the page is visible. Re-rendering only refills the columns, so the sideways scroll
// position survives it.
const REFRESH_MS = 60000;

// The "How it works" trailer the pricing pages, the upgrade page, /live-pricing-options and
// the landing all play from the calendar's top bar today.
const HOW_IT_WORKS_VIDEO = 'p3ko3ab73w';

// The top bar's buttons, left-aligned: inside a modal the right corner belongs to the modal's
// close button. "See Pricing" is opt-in (data-see-pricing), for pages that are not pricing pages
// themselves; its href is resolved again on click, once global.js knows the currency.
function actionsHtml(opts) {
  if (!opts.interactive) return '';
  const how = opts.howItWorks
    ? '<button type="button" class="live-how-btn"><span class="live-how-disc" aria-hidden="true"><span class="live-how-tri"></span></span>How it works</button>'
    : '';
  const pricing = opts.seePricing
    ? `<a class="live-pricing-btn" href="${escapeHtml(pricingUrl(opts.ctaUrl))}">See Pricing</a>`
    : '';
  return how || pricing
    ? `<div class="live-sched-actions">${how}${pricing}</div>`
    : '';
}

export function mountCalendar(target, opts) {
  if (target.__dmcLiveSchedule) return target.__dmcLiveSchedule;

  // The container's own content (a placeholder, a previous copy of the calendar) goes.
  target.innerHTML = '';
  const host = document.createElement('div');
  host.className = 'dmc-live-schedule';
  if (opts.scroll === 'self') host.style.height = '100%';
  target.appendChild(host);
  const root = host.attachShadow({ mode: 'open' });

  const daysOrder = Array.from(
    { length: 7 },
    (_, i) => (getWeekStartIndex() + i) % 7,
  );
  // The top bar holds only the buttons ("How it works", "See Pricing"): no title and no
  // local-time line. With no buttons there is no bar at all, and the day row tops the panel.
  const actions = actionsHtml(opts);
  const rootClass =
    'live-root' +
    (opts.scroll === 'self' ? ' live-root--self' : '') +
    (opts.interactive ? '' : ' live-root--static');

  root.innerHTML =
    `<style>${calendarCss}</style>` +
    `<div class="${rootClass}" style="--live-sticky-top:${Number(opts.stickyTop) || 0}px">` +
    '<div class="live-panel">' +
    `<div class="live-panel-sticky${actions ? '' : ' live-panel-sticky--bare'}">` +
    (actions ? `<div class="live-sched-top-bar">${actions}</div>` : '') +
    '<div class="live-week-head-wrap"><div class="live-week-head"></div></div>' +
    '</div>' +
    '<div class="live-loading">Loading <span class="live-spinner"></span></div>' +
    '<div class="live-schedule-grid-container" hidden>' +
    '<div class="live-week-scroller"><div class="live-week-grid">' +
    daysOrder
      .map((d) => `<div class="live-day-stack" data-day="${d}"></div>`)
      .join('') +
    '</div></div></div>' +
    '</div></div>';

  const rootEl = root.querySelector('.live-root');
  const head = root.querySelector('.live-week-head');
  const scroller = root.querySelector('.live-week-scroller');
  const gridView = root.querySelector('.live-schedule-grid-container');
  const loading = root.querySelector('.live-loading');
  const stacks = {};
  root
    .querySelectorAll('.live-day-stack')
    .forEach((s) => (stacks[s.dataset.day] = s));

  // The day row sits outside the horizontal scroller (it must, to stay sticky), so it is
  // slid by hand to mirror the grid's scrollLeft.
  scroller.addEventListener(
    'scroll',
    () => (head.style.transform = `translateX(-${scroller.scrollLeft}px)`),
    { passive: true },
  );

  // /class-ratings is ~300 KB: fetch it when someone shows interest in the calendar, not on
  // every page view (opening a class fetches it too, if this never fired).
  if (opts.interactive) {
    rootEl.addEventListener('pointerover', () => loadRatings(), { once: true });
    rootEl.addEventListener('focusin', () => loadRatings(), { once: true });
  }

  let raw = null;
  let byId = {};
  let instById = {};

  function render() {
    const { instructors, classes } = buildSchedule(raw);
    instById = {};
    instructors.forEach((i) => (instById[i.id] = i));
    byId = {};

    const today = new Date().getDay();
    head.innerHTML = daysOrder
      .map(
        (d) =>
          `<div class="live-day-name${d === today ? ' live-day-name--today' : ''}">${DAY_NAMES[d]}</div>`,
      )
      .join('');
    Object.values(stacks).forEach((s) => (s.innerHTML = ''));

    // Cards are appended in order (there is no hour grid placing them), so the list has to
    // BE in order: minutes from the visitor's local midnight.
    const withLocal = classes.map((c) => ({ ...c, localtime: localTimeOf(c) }));
    const minutesOf = (c) => {
      const [h, m] = c.localtime.hour.split(':');
      return parseInt(h) * 60 + parseInt(m);
    };
    const nextClassId = findNextClassId(withLocal);

    withLocal
      .sort((a, b) => minutesOf(a) - minutesOf(b))
      .forEach((cls) => {
        const isCanceled = cls.active === false;
        // A suspension with no (valid) resume date is open-ended: a "Back soon" card would
        // sit on the grid forever, so it stays hidden (the Library's rule).
        const resumeShort = isCanceled
          ? formatResumeDate(cls.resumesOn, 'short')
          : '';
        if (isCanceled && !resumeShort) return;
        const stack = stacks[cls.localtime.day];
        if (!stack) return;

        byId[cls.id] = cls;
        const inst = instById[cls.idInstructor];
        const instName = escapeHtml(inst ? inst.firstName : '');
        const [bg, fg, outline] = tagColors(cls.category);

        // One state slot, in priority order: suspended > live now > next > instructor.
        const foot = isCanceled
          ? `<span class="live-card-foot live-card-foot--canceled">Back on ${escapeHtml(resumeShort)}</span>`
          : isClassNow(cls)
            ? '<span class="live-card-foot live-card-foot--live"><span class="live-now-dot"></span>Live Now</span>'
            : cls.id === nextClassId
              ? '<span class="live-card-foot live-card-foot--next">Next</span>'
              : `<span class="live-card-foot">${instName}</span>`;

        const card = document.createElement('div');
        card.className =
          'live-schedule-card' + (isCanceled ? ' is-canceled' : '');
        card.dataset.slot = cls.id;
        if (opts.interactive) {
          card.tabIndex = 0;
          card.setAttribute('role', 'button');
        }
        card.innerHTML =
          '<div class="live-card-head">' +
          `<span class="live-card-time-top">${formatTo12h(cls.localtime.hour)}</span>` +
          (isCanceled
            ? `<span class="live-card-tag" style="color:${outline}">${escapeHtml(cls.category)}</span>`
            : `<span class="live-card-tag" style="background:${bg};color:${fg}">${escapeHtml(cls.category)}</span>`) +
          '</div>' +
          `<div class="live-card-title">${escapeHtml(cls.className)}</div>` +
          '<div class="live-card-foot-row">' +
          `<span class="live-card-duration">${Number(cls.duration) || 0}min</span>` +
          foot +
          '</div>';
        stack.appendChild(card);
      });

    loading.hidden = true;
    gridView.hidden = false;
  }

  function open(card) {
    const cls = byId[card.dataset.slot];
    if (cls)
      openDetail(cls, instById[cls.idInstructor], { ...opts, trigger: card });
  }

  if (opts.interactive) {
    rootEl.addEventListener('click', (e) => {
      const how = e.target.closest('.live-how-btn');
      if (how) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'clicked_how_it_works_schedule',
          schedule_surface: opts.surface,
        });
        openVideo(HOW_IT_WORKS_VIDEO, { ...opts, trigger: how });
        return;
      }
      const pricing = e.target.closest('.live-pricing-btn');
      if (pricing) {
        pricing.href = pricingUrl(opts.ctaUrl);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'clicked_see_pricing_schedule',
          schedule_surface: opts.surface,
        });
        return;
      }
      const card = e.target.closest('.live-schedule-card');
      if (card) open(card);
    });
    rootEl.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const card = e.target.closest('.live-schedule-card');
      if (!card) return;
      e.preventDefault();
      open(card);
    });
  }

  let timer = null;
  const instance = {
    refresh() {
      if (raw) render();
    },
    destroy() {
      clearInterval(timer);
      host.remove();
      delete target.__dmcLiveSchedule;
    },
  };
  target.__dmcLiveSchedule = instance;

  loadSchedule()
    .then((data) => {
      raw = data;
      render();
      timer = setInterval(() => {
        if (!host.isConnected) return instance.destroy();
        if (!document.hidden) render();
      }, REFRESH_MS);
    })
    .catch((err) => {
      console.error('[dmc-live-schedule] schedule load failed:', err);
      loading.outerHTML =
        '<div class="live-error">Schedule unavailable. Please try again later.</div>';
    });

  return instance;
}
